import { deleteSupplierThunk, getSuppliersThunk } from '@redux/features/supplier.slice';
import { AnimatedListSkeleton, AnimatedTable, AnimatedTableBody, AnimatedTableRow } from 'components/animations';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import BreadCrumb from 'Common/BreadCrumb';
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal';
import React, { FC, useEffect, useState } from 'react';
import ButtonBase from 'components/ui/buttons/ButtonBase';
import AddSupplierModal from './AddSupplierModal';
import UpdateSupplierModal from './UpdateSupplierModal';

const Supplier: FC = () => {
    const [page, setPage] = useState<number>(0);
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
    const [showAddModal, setShowAddModal] = useState<boolean>(false);
    const [showUpdateModal, setShowUpdateModal] = useState<boolean>(false);
    const [supplierId, setSupplierId] = useState<number>();

    const dispatch = useAppDispatch();
    const { suppliers, loading } = useAppSelector(
        (state: RootState) => (state as any).supplierState || { suppliers: [], loading: false }
    );

    const changePage = (p: number) => setPage(p);

    useEffect(() => {
        dispatch(getSuppliersThunk());
    }, [page, dispatch]);

    const handleConfirmDelete = async () => {
        if (supplierId) {
            dispatch(deleteSupplierThunk(supplierId)).then((response: any) => {
                if (response.meta.requestStatus === 'fulfilled') {
                    setShowDeleteModal(false);
                    setSupplierId(undefined);
                    dispatch(getSuppliersThunk());
                }
            });
        }
    };

    const handleDelete = (id: number) => {
        setSupplierId(id);
        setShowDeleteModal(true);
    };

    const handleUpdate = (id: number) => {
        setSupplierId(id);
        setShowUpdateModal(true);
    };

    return (
        <>
            <UpdateSupplierModal
                show={showUpdateModal}
                handleClose={() => setShowUpdateModal(false)}
                supplierId={supplierId || 0}
            />
            <AddSupplierModal show={showAddModal} handleClose={() => setShowAddModal(false)} />
            <ConfirmationModal
                show={showDeleteModal}
                handleClose={() => {
                    setShowDeleteModal(false);
                    setSupplierId(undefined);
                }}
                onConfirm={handleConfirmDelete}
                title="Delete Supplier"
                message="Are you sure you want to delete this supplier? This action cannot be undone."
                confirmButtonText="Delete"
                cancelButtonText="Cancel"
                id={supplierId}
            />
            <BreadCrumb pageTitle={`Suppliers List`} />
            <section className="py-5">
                <div className=" p-3 flex flex-col gap-2 bg-[#fff]">
                    <div className="flex w-full items-center justify-between">
                        <h3 className="font-extrabold text-[#bd902e]">Suppliers List</h3>
                        <ButtonBase innerText="Add Supplier" onClick={() => setShowAddModal(true)} />
                    </div>
                    {loading ? (
                        <AnimatedListSkeleton rows={8} />
                    ) : (
                        <>
                            <div>
                                <div className="overflow-x-auto">
                                    <AnimatedTable className="table-custom w-full border-separate border-spacing-y-1 whitespace-nowrap">
                                        <thead className="ltr:text-left rtl:text-right">
                                            <tr
                                                className="relative rounded-md bg-slate-50 after:absolute after:bottom-0
                    after:left-0 after:top-0 after:border-l-2
                    after:border-transparent [&.active]:after:border-custom-500"
                                            >
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">ID</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Email</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">DUNS Number</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Supplier GLN</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Supplier SGLN</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Business Name</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Business Info</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">
                                                    Primary Contact
                                                </th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Phone</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Licence No</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Licence Exp</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">
                                                    Federal Tax ID
                                                </th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Shipping City</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">
                                                    Shipping State
                                                </th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Shipping Zip</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Billing City</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Billing State</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Billing Zip</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Fax</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">DEA</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Reg. Date</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Pharmacy Lic</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase text-center">
                                                    Actions
                                                </th>
                                            </tr>
                                        </thead>
                                        <AnimatedTableBody loading={loading} itemCount={(suppliers || []).length} animationKey={`suppliers-${page}`}>
                                            {(suppliers || []).map((s: any) => (
                                                <AnimatedTableRow
                                                    rowKey={s?.id}
                                                    detailRecord={s}
                                                    className="relative rounded-md bg-slate-50 after:absolute after:bottom-0
                        after:left-0 after:top-0 after:border-l-2 hover:bg-[#bd902e10]
                        after:border-transparent [&.active]:after:border-custom-500"
                                                >
                                                    <td className="px-3.5 py-2.5">{s?.id || `-`}</td>
                                                    <td className="px-3.5 py-2.5">{s?.email || `-`}</td>
                                                    <td className="px-3.5 py-2.5">{s?.dunsNumber || `-`}</td>
                                                    <td className="px-3.5 py-2.5 font-mono text-sm">{s?.supplierGln || `-`}</td>
                                                    <td className="px-3.5 py-2.5 font-mono text-xs">{s?.supplierSgln || `-`}</td>
                                                    <td className="px-3.5 py-2.5">{s?.businessName || s?.account?.businessName || `-`}</td>
                                                    <td className="px-3.5 py-2.5">{s?.account?.businessInfo || `-`}</td>
                                                    <td className="px-3.5 py-2.5">
                                                        {s?.account?.primaryContactName || `-`}
                                                    </td>
                                                    <td className="px-3.5 py-2.5">{s?.account?.phoneNumber || `-`}</td>
                                                    <td className="px-3.5 py-2.5">
                                                        {s?.account?.businessLicenceNumber || `-`}
                                                    </td>
                                                    <td className="px-3.5 py-2.5">
                                                        {s?.account?.businessLicenceExpirationDate || `-`}
                                                    </td>
                                                    <td className="px-3.5 py-2.5">{s?.account?.federalTaxID || `-`}</td>
                                                    <td className="px-3.5 py-2.5">{s?.account?.shippingCity || `-`}</td>
                                                    <td className="px-3.5 py-2.5">
                                                        {s?.account?.shippingState || `-`}
                                                    </td>
                                                    <td className="px-3.5 py-2.5">
                                                        {s?.account?.shippingZipCode || `-`}
                                                    </td>
                                                    <td className="px-3.5 py-2.5">{s?.account?.billingCity || `-`}</td>
                                                    <td className="px-3.5 py-2.5">{s?.account?.billingState || `-`}</td>
                                                    <td className="px-3.5 py-2.5">
                                                        {s?.account?.billingZipCode || `-`}
                                                    </td>
                                                    <td className="px-3.5 py-2.5">{s?.account?.faxNumber || `-`}</td>
                                                    <td className="px-3.5 py-2.5">{s?.account?.deaNumber || `-`}</td>
                                                    <td className="px-3.5 py-2.5">
                                                        {s?.account?.registrationDate || `-`}
                                                    </td>
                                                    <td className="px-3.5 py-2.5">
                                                        {s?.account?.statePharmacyLicenseNumber || `-`}
                                                    </td>
                                                    <td className="px-3.5 py-2.5">
                                                        <div className="flex flex-row justify-center items-center gap-2 w-full">
                                                            <button
                                                                className="group flex px-4 py-2 text-xs font-semibold items-center justify-center
                                                        rounded-md border border-red-500 text-red-600
                                                        bg-white transition-all duration-300 ease-linear hover:bg-red-50 hover:border-red-600 hover:scale-105 active:scale-95"
                                                                onClick={() => {
                                                                    handleDelete(s?.id);
                                                                }}
                                                            >
                                                                Delete
                                                            </button>
                                                            <button
                                                                className="group flex px-4 py-2 text-xs font-semibold items-center justify-center
                                                        rounded-md border border-yellow-600 text-yellow-600
                                                        bg-white transition-all duration-300 ease-linear hover:bg-yellow-50 hover:border-yellow-700 hover:scale-105 active:scale-95"
                                                                onClick={() => {
                                                                    handleUpdate(s?.id);
                                                                }}
                                                            >
                                                                Update
                                                            </button>
                                                        </div>
                                                    </td>
                                                </AnimatedTableRow>
                                            ))}
                                        </AnimatedTableBody>
                                    </AnimatedTable>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </section>
        </>
    );
};

export default Supplier;
